import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FileDown, Eye, FileText, Calendar, DollarSign, Users, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { holdingsAPI, formatDate, formatCurrency } from "../../services/api";

const TokenAllocationOutcome = () => {
    const navigate = useNavigate();
    const [selectedBond, setSelectedBond] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [productTypeFilter, setProductTypeFilter] = useState('');
    const pageSize = 10;

    // Fetch bonds with holdings
    const {
        data: bondsData,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ["bondsWithHoldings", currentPage, productTypeFilter],
        queryFn: async () => {
            const response = await holdingsAPI.getBondsWithHoldings({
                page: currentPage,
                limit: pageSize,
                productType: productTypeFilter
            });
            return response.data;
        },
    });

    const bonds = bondsData?.data?.bonds || [];
    const totalPages = bondsData?.data?.totalPages || 1;
    const totalBonds = bondsData?.data?.total || 0;

    const productTypes = [
        { value: '', label: 'All Product Types' },
        { value: 'Fixed Notes', label: 'Conventional' },
        { value: 'Sukuk', label: 'Sukuk' },
    ];

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleProductTypeChange = (e) => {
        setProductTypeFilter(e.target.value);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    // Generate report for a specific bond
    const handleGenerateReport = async (bond) => {
        try {
            setSelectedBond(bond);
            const response = await holdingsAPI.getAllocationReport(bond._id);
            setReportData(response.data.data);
            setShowReportModal(true);
        } catch (error) {
            console.error("Error generating report:", error);
            alert("Failed to generate report. Please try again.");
        }
    };

    // Download report as CSV
    const handleDownloadReport = () => {
        if (!reportData) return;

        const headers = ["Holder Name", "Email", "Wallet Address", "Bond Amount", "Holdings Start Date"];
        const rows = reportData.tokenHolders.map(holder => [
            holder.name,
            holder.email,
            holder.walletAddress || 'N/A',
            holder.tokenAmount,
            formatDate(holder.holdingsStartDate)
        ]);

        const csvContent = [
            [`Token Allocation Report - ${reportData.bond.bondName}`],
            [`Bond Number: ${reportData.bond.bondNumber}`],
            [`ISIN: ${reportData.bond.isin || 'N/A'}`],
            [`Generated: ${formatDate(reportData.generatedAt)}`],
            [],
            headers,
            ...rows
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `allocation-report-${reportData.bond.bondNumber}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'draft': 'bg-gray-100 text-gray-800',
            'pending_approval': 'bg-yellow-100 text-yellow-800',
            'approved': 'bg-green-100 text-green-800',
            'rejected': 'bg-red-100 text-red-800',
            'active': 'bg-blue-100 text-blue-800',
            'matured': 'bg-purple-100 text-purple-800',
        };
        return statusColors[status] || 'bg-gray-100 text-gray-800';
    };

    const ReportModal = () => {
        if (!showReportModal || !reportData) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex justify-between items-start">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    Token Allocation Report
                                </h2>
                                <div className="space-y-1 text-sm text-gray-600">
                                    <p><strong>Bond:</strong> {reportData.bond.bondName}</p>
                                    <p><strong>Bond Number:</strong> {reportData.bond.bondNumber}</p>
                                    <p><strong>ISIN:</strong> {reportData.bond.isin || 'N/A'}</p>
                                    <p><strong>Product Type:</strong> {reportData.bond.productType === "Fixed Notes" ? "Conventional" : reportData.bond.productType}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowReportModal(false)}
                                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="p-6 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Total Holders</p>
                                        <p className="text-2xl font-bold text-gray-900">{reportData.totalHolders}</p>
                                    </div>
                                    <Users className="w-8 h-8 text-blue-500" />
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Tokens Allocated</p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {reportData.totalTokensAllocated.toLocaleString()}
                                        </p>
                                    </div>
                                    <DollarSign className="w-8 h-8 text-green-500" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Token Holders Table */}
                    <div className="flex-1 overflow-auto p-6">
                        <div className="mb-4 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">Current Token Holders</h3>
                            <button
                                onClick={handleDownloadReport}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <FileDown className="w-4 h-4" />
                                Download CSV
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Holder Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Wallet Address
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Bond Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Holdings Start
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reportData.tokenHolders.map((holder, index) => (
                                        <tr key={holder.holderId || index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {holder.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {holder.email}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                                {holder.walletAddress ? (
                                                    <span className="text-xs">
                                                        {holder.walletAddress.substring(0, 10)}...
                                                        {holder.walletAddress.substring(holder.walletAddress.length - 8)}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                                                {holder.tokenAmount.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(holder.holdingsStartDate)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 bg-gray-50 text-center text-sm text-gray-500">
                        Generated on {formatDate(reportData.generatedAt)}
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading bonds...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Error loading bonds: {error.message}</p>
                    <button
                        onClick={() => refetch()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6">
                        <button
                            onClick={() => navigate('/issuer/documents')}
                            className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Documents
                        </button>
                        <div className="flex items-center gap-3 mb-2">
                            <FileText className="w-8 h-8 text-blue-600" />
                            <h1 className="text-3xl font-bold text-gray-900">
                                Token Allocation Outcome Report
                            </h1>
                        </div>
                        <p className="text-gray-500 mt-2">
                            View and generate detailed reports of token allocation outcomes for your bonds
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Filters */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div>
                            <label htmlFor="productType" className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Product Type
                            </label>
                            <select
                                id="productType"
                                value={productTypeFilter}
                                onChange={handleProductTypeChange}
                                className="block w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            >
                                {productTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="text-sm text-gray-600">
                        Showing {bonds.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to {Math.min(currentPage * pageSize, totalBonds)} of {totalBonds} bonds
                    </div>
                </div>
                {bonds.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Bonds Available</h3>
                        <p className="text-gray-500">
                            There are no bonds with token allocations to display.
                        </p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Bond Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Product Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Holders
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {bonds.map((bond) => (
                                        <tr key={bond._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {bond.bondName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {bond.bondNumber}
                                                    </div>
                                                    {bond.isin && (
                                                        <div className="text-xs text-gray-400">
                                                            ISIN: {bond.isin}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">
                                                    {bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(bond.status)}`}>
                                                    {bond.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <Users className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {bond.totalHolders}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <button
                                                    onClick={() => handleGenerateReport(bond)}
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={bond.totalHolders === 0}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    Generate Report
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-gray-700">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </button>
                                        <div className="flex gap-1">
                                            {[...Array(totalPages)].map((_, idx) => {
                                                const page = idx + 1;
                                                // Show first page, last page, current page, and pages around current
                                                if (
                                                    page === 1 ||
                                                    page === totalPages ||
                                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`px-3 py-2 border rounded-md text-sm font-medium ${currentPage === page
                                                                ? 'bg-blue-600 text-white border-blue-600'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                } else if (
                                                    page === currentPage - 2 ||
                                                    page === currentPage + 2
                                                ) {
                                                    return (
                                                        <span key={page} className="px-2 py-2 text-gray-500">
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Report Modal */}
            <ReportModal />
        </div>
    );
};

export default TokenAllocationOutcome;
